import { useCallback, useMemo } from "react";
import { useMsal } from "@azure/msal-react";
import axios, { AxiosInstance } from "axios";
import { apiConfig } from "../config/authConfig";
import {
  OrderData,
  ApprovalRequest,
  ApprovalDetails,
  ApiResponse,
  CFROIResponse,
} from "../types/api";
import { MockApiService } from "../services/mockApi";


/** An error whose message comes from the API and can be shown to the user. */
export class ApiError extends Error {}

// Entity a global admin is working in, sent as X-Entity-ID. An `?entity=` URL
// parameter (used by email links) selects it; it is kept for the browser tab.
const ACTIVE_ENTITY_KEY = "purchaseApproval.activeEntity";
let activeEntity: string | null = (() => {
  try {
    const fromUrl = new URLSearchParams(window.location.search).get("entity");
    if (fromUrl) sessionStorage.setItem(ACTIVE_ENTITY_KEY, fromUrl);
    return fromUrl || sessionStorage.getItem(ACTIVE_ENTITY_KEY);
  } catch {
    return null;
  }
})();

export function getActiveEntity(): string | null {
  return activeEntity;
}

/** Selects the entity for subsequent API calls; null returns to the user's own. */
export function setActiveEntity(entity: string | null): void {
  activeEntity = entity;
  try {
    if (entity) sessionStorage.setItem(ACTIVE_ENTITY_KEY, entity);
    else sessionStorage.removeItem(ACTIVE_ENTITY_KEY);
  } catch {
    // Storage unavailable: the selection lasts until the page reloads.
  }
}

interface AdminApprovals {
  suppliers?: string[];
  requests: Array<{
    id: string;
    orderNumber: string;
    supplier?: string;
    erpPaymentTerms?: string | null;
    orderType: string;
    requestedBy: string;
    requestedByName: string;
    purchasingManager: string;
    portfolioCfroi: number;
    totalInvestment: number;
    status: string;
    createdAt: string;
    expiresAt: string;
  }>;
  count: number;
}

interface AdminStatistics {
  total_requests: number;
  pending_requests: number;
  approved_requests: number;
  rejected_requests: number;
  expired_requests: number;
  avg_cfroi: number;
  total_investment: number;
}

export type ApprovalSummary = AdminApprovals["requests"][number];

// A bounded, in-memory cache; order data is never persisted to disk.
let adminOverviewCache: { key: string; data: AdminApprovals; expiresAt: number } | undefined;
let adminOverviewGeneration = 0;
function invalidateAdminOverview() {
  adminOverviewCache = undefined;
  adminOverviewGeneration++;
}

interface AdminSettings {
  interestRate: number; // Global WACC as decimal, e.g., 0.15 = 15%
  entityId: string; // Entity whose approvers are returned
  level1ApproverEmails: string[];
  level2ApproverEmails: string[];
  isGlobalAdmin?: boolean;
  entities?: string[]; // Global admins only
  entityDomains?: Record<string, string>; // Global admins only: email domain -> entity
}

interface CurrentUser {
  email: string;
  name: string;
  role: string;
  isActive: boolean;
  isAdmin: boolean;
  isGlobalAdmin: boolean;
  entityId: string; // Entity the session acts on
}

interface ApprovalActionRequest {
  action: "approve" | "reject";
  approverEmail: string;
  approverName: string;
  notes?: string;
  reason?: string;
}

interface ApprovalActionResponse {
  requestId: string;
  action: string;
  status: string;
  approverName: string;
  message: string;
  processedAt: string;
}

interface ApiClient {
  getMyRequests: (signal?: AbortSignal) => Promise<AdminApprovals>;
  getOrderData: (orderNumber: string) => Promise<OrderData>;
  getCFROIData: (
    orderNumber: string,
    calculationParams?: {
      orderType: string;
      paymentTerms: number;
      interestRate?: number;
      shippingTime?: number;
    }
  ) => Promise<CFROIResponse>;
  requestApproval: (request: ApprovalRequest) => Promise<void>;
  getApprovalDetails: (requestId: string) => Promise<ApprovalDetails>;
  getAdminApprovals: (params?: { dateFrom?: string; dateTo?: string; requesterName?: string; supplier?: string }, signal?: AbortSignal, preferCache?: boolean) => Promise<AdminApprovals>;
  getAdminStatistics: () => Promise<AdminStatistics>;
  getAdminSettings: () => Promise<AdminSettings>;
  updateAdminSettings: (settings: Partial<AdminSettings>) => Promise<AdminSettings>;
  getCurrentUser: () => Promise<CurrentUser>;
  approveRequest: (requestId: string, notes?: string) => Promise<ApprovalActionResponse>;
  rejectRequest: (requestId: string, reason: string, notes?: string) => Promise<ApprovalActionResponse>;
}

// Check if we're in development mode and should use mock data
const isDevelopment =
  import.meta.env.DEV && !import.meta.env.VITE_API_BASE_URL?.startsWith("http");

export const useApiClient = (): ApiClient => {
  const { instance, accounts } = useMsal();

  const getAuthenticatedAxios =
    useCallback(async (): Promise<AxiosInstance> => {
      let accessToken = "";

      if (accounts.length > 0) {
        try {
          const response = await instance.acquireTokenSilent({
            scopes: apiConfig.scopes,
            account: accounts[0],
          });
          accessToken = response.accessToken;
        } catch (error) {
          console.error("Token acquisition failed:", error);
          // Fallback to interactive token acquisition
          try {
            const response = await instance.acquireTokenPopup({
              scopes: apiConfig.scopes,
              account: accounts[0],
            });
            accessToken = response.accessToken;
          } catch (popupError) {
            console.error("Interactive token acquisition failed:", popupError);
            throw new Error("Authentication failed");
          }
        }
      }

      return axios.create({
        baseURL: apiConfig.baseUrl,
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "Content-Type": "application/json",
          ...(activeEntity ? { "X-Entity-ID": activeEntity } : {}),
        },
      });
    }, [instance, accounts]);

  const getCFROIData = useCallback(
    async (
      orderNumber: string,
      calculationParams?: {
        orderType: string;
        paymentTerms: number;
        interestRate?: number; // Optional override for scenario testing (e.g., 0.15 for 15%)
        shippingTime?: number; // Optional override for shipping time in days
      }
    ): Promise<CFROIResponse> => {
      // Use mock data in development mode
      if (isDevelopment) {
        console.log("🔧 Development mode: Using mock CFROI data");
        console.log("📊 Calculation params:", calculationParams);
        // For now, return a mock CFROI response
        return {
          requestId: `mock-${Date.now()}`,
          orderNumber: orderNumber,
          success: true,
          calculationTime: new Date().toISOString(),
          processingTimeMs: 100,
          portfolioCfroi: 0.15,
          skuPaymentSeries: [],
        };
      }

      const axiosInstance = await getAuthenticatedAxios();

      try {
        // Include calculation parameters in the request
        const params: any = {};
        if (calculationParams) {
          if (calculationParams.orderType) {
            params.orderType = calculationParams.orderType;
          }
          if (calculationParams.paymentTerms !== undefined) {
            params.paymentTerms = calculationParams.paymentTerms;
          }
          if (calculationParams.interestRate !== undefined) {
            params.interestRate = calculationParams.interestRate;
          }
          if (calculationParams.shippingTime !== undefined) {
            params.shippingTime = calculationParams.shippingTime;
          }
        }

        const response = await axiosInstance.get<ApiResponse<CFROIResponse>>(
          `/api/v1/orders/${orderNumber}`,
          { params }
        );

        if (response.data.success && response.data.data) {
          return response.data.data;
        } else {
          throw new Error(response.data.error || "Failed to fetch CFROI data");
        }
      } catch (error) {
        console.error("Error fetching CFROI data:", error);
        throw error;
      }
    },
    [getAuthenticatedAxios]
  );

  const getOrderData = useCallback(
    async (orderNumber: string): Promise<OrderData> => {
      // Use mock data in development mode
      if (isDevelopment) {
        console.log("🔧 Development mode: Using mock data");
        return MockApiService.getOrderData(orderNumber);
      }

      // For now, we'll convert CFROI data to OrderData format
      // In the future, you might want to create a separate endpoint for order metadata
      const cfroiData = await getCFROIData(orderNumber);

      // Convert CFROI response to OrderData format for UI compatibility
      const orderData: OrderData = {
        orderNumber: cfroiData.orderNumber,
        supplier: cfroiData.supplier,
        erpPaymentTerms: cfroiData.erpPaymentTerms,
        purchasingEntity: "Caseking", // This data isn't in CFROI response
        purchasingManager: "purchasing.manager@caseking.de", // Use email format
        paymentTerms: 30, // Default value
        cfroi: cfroiData.portfolioCfroi || 0, // Use portfolio CFROI or default to 0
        skus:
          cfroiData.skuPaymentSeries?.map((sku) => ({
            skuId: sku.position.sku,
            itemName: sku.position.itemName,
            purchasingVolume: sku.investment,
            amount: sku.position.quantity,
            currentStock: sku.currentStock,
            cfroi: sku.cfroi,
            npv: sku.npv || 0, // Use NPV from payment series or default to 0
            hasSalesData: sku.hasSalesData,
          })) || [],
      };

      return orderData;
    },
    [getCFROIData]
  );

  const requestApproval = useCallback(
    async (request: ApprovalRequest): Promise<void> => {
      // Use mock API in development mode
      if (isDevelopment) {
        console.log("🔧 Development mode: Using mock approval service");
        return MockApiService.submitApproval();
      }

      const axiosInstance = await getAuthenticatedAxios();

      try {
        const response = await axiosInstance.post<ApiResponse<void>>(
          "/api/v1/approval/requests",
          request
        );

        if (!response.data.success) {
          throw new Error(
            response.data.error || "Failed to submit approval request"
          );
        }
        invalidateAdminOverview();
      } catch (error) {
        console.error("Error submitting approval request:", error);
        // Surface the server's explanation (e.g. no approvers configured).
        const serverMessage = axios.isAxiosError(error) ? error.response?.data?.error : undefined;
        if (typeof serverMessage === "string" && serverMessage) {
          throw new ApiError(serverMessage);
        }
        throw error;
      }
    },
    [getAuthenticatedAxios]
  );

  const getApprovalDetails = useCallback(
    async (requestId: string): Promise<ApprovalDetails> => {
      // Use mock data in development mode
      if (isDevelopment) {
        console.log("🔧 Development mode: Using mock approval details");
        return {
          requestId: requestId,
          orderNumber: "ORD-2024-001",
          status: "pending",
          requestedBy: "user@caseking.de",
          requestedByName: "Test User",
          portfolioCfroi: 0.15,
          totalInvestment: 50000,
          orderPositions: [
            {
              sku: "SKU-001",
              itemName: "Gaming Headset Pro",
              quantity: 100,
              price: 50.0,
            },
          ],
          createdAt: new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString(), // 1 day ago
          expiresAt: new Date(
            Date.now() + 6 * 24 * 60 * 60 * 1000
          ).toISOString(), // 6 days from now
        };
      }

      const axiosInstance = await getAuthenticatedAxios();

      try {
        const response = await axiosInstance.get<ApiResponse<ApprovalDetails>>(
          `/api/v1/approval/requests/${requestId}`
        );

        if (response.data.success && response.data.data) {
          return response.data.data;
        } else {
          throw new Error(
            response.data.error || "Failed to fetch approval details"
          );
        }
      } catch (error) {
        console.error("Error fetching approval details:", error);
        throw error;
      }
    },
    [getAuthenticatedAxios]
  );

  const getAdminApprovals = useCallback(async (
    params?: { dateFrom?: string; dateTo?: string; requesterName?: string; supplier?: string },
    signal?: AbortSignal,
    preferCache = false
  ): Promise<AdminApprovals> => {
    const account = accounts[0]; // Same account used by getAuthenticatedAxios.
    const key = JSON.stringify([apiConfig.baseUrl, account?.homeAccountId, account?.localAccountId, activeEntity,
      params?.dateFrom ?? "", params?.dateTo ?? "", params?.requesterName ?? "", params?.supplier ?? ""]);
    if (account && preferCache && !signal?.aborted &&
        adminOverviewCache?.key === key && adminOverviewCache.expiresAt > Date.now()) {
      return adminOverviewCache.data;
    }
    const generation = ++adminOverviewGeneration;
    const axiosInstance = await getAuthenticatedAxios();

    try {
      const queryParams: Record<string, string> = {};
      if (params?.dateFrom) queryParams.date_from = params.dateFrom;
      if (params?.dateTo) queryParams.date_to = params.dateTo;
      if (params?.requesterName) queryParams.requester_name = params.requesterName;
      if (params?.supplier) queryParams.supplier = params.supplier;

      const response = await axiosInstance.get<AdminApprovals>(
        "/api/v1/admin/approvals",
        { params: queryParams, signal }
      );
      if (account && !signal?.aborted && generation === adminOverviewGeneration) {
        adminOverviewCache = { key, data: response.data, expiresAt: Date.now() + 5 * 60_000 };
      }
      return response.data;
    } catch (error) {
      console.error("Error fetching admin approvals:", error);
      throw error;
    }
  }, [getAuthenticatedAxios, accounts]);

  const getAdminStatistics = useCallback(async (): Promise<AdminStatistics> => {
    const axiosInstance = await getAuthenticatedAxios();

    try {
      const response = await axiosInstance.get<AdminStatistics>(
        "/api/v1/admin/statistics"
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching admin statistics:", error);
      throw error;
    }
  }, [getAuthenticatedAxios]);

  const getMyRequests = useCallback(async (signal?: AbortSignal): Promise<AdminApprovals> => {
    const client = await getAuthenticatedAxios();
    const response = await client.get<AdminApprovals>("/api/v1/user/requests", { signal });
    return response.data;
  }, [getAuthenticatedAxios]);

  const getCurrentUser = useCallback(async (): Promise<CurrentUser> => {
    const axiosInstance = await getAuthenticatedAxios();

    try {
      const response = await axiosInstance.get<CurrentUser>("/api/v1/user/me");
      return response.data;
    } catch (error) {
      console.error("Error fetching current user:", error);
      // e.g. 403 for accounts whose email domain is not mapped to an entity
      const serverMessage = axios.isAxiosError(error) ? error.response?.data?.message : undefined;
      if (typeof serverMessage === "string" && serverMessage) {
        throw new ApiError(serverMessage);
      }
      throw error;
    }
  }, [getAuthenticatedAxios]);

  const approveRequest = useCallback(
    async (
      requestId: string,
      notes?: string
    ): Promise<ApprovalActionResponse> => {
      const axiosInstance = await getAuthenticatedAxios();

      try {
        // Get current user info for approver details
        const currentUser = await getCurrentUser();

        const actionRequest: ApprovalActionRequest = {
          action: "approve",
          approverEmail: currentUser.email,
          approverName: currentUser.name,
          notes: notes,
        };

        const response = await axiosInstance.post<
          ApiResponse<ApprovalActionResponse>
        >(`/api/v1/approval/requests/${requestId}/actions`, actionRequest);

        if (response.data.success && response.data.data) {
          invalidateAdminOverview();
          return response.data.data;
        } else {
          throw new Error(response.data.error || "Failed to approve request");
        }
      } catch (error) {
        console.error("Error approving request:", error);
        throw error;
      }
    },
    [getAuthenticatedAxios, getCurrentUser]
  );

  const rejectRequest = useCallback(
    async (
      requestId: string,
      reason: string,
      notes?: string
    ): Promise<ApprovalActionResponse> => {
      const axiosInstance = await getAuthenticatedAxios();

      try {
        // Get current user info for approver details
        const currentUser = await getCurrentUser();

        const actionRequest: ApprovalActionRequest = {
          action: "reject",
          approverEmail: currentUser.email,
          approverName: currentUser.name,
          reason: reason,
          notes: notes,
        };

        const response = await axiosInstance.post<
          ApiResponse<ApprovalActionResponse>
        >(`/api/v1/approval/requests/${requestId}/actions`, actionRequest);

        if (response.data.success && response.data.data) {
          invalidateAdminOverview();
          return response.data.data;
        } else {
          throw new Error(response.data.error || "Failed to reject request");
        }
      } catch (error) {
        console.error("Error rejecting request:", error);
        throw error;
      }
    },
    [getAuthenticatedAxios, getCurrentUser]
  );

  const getAdminSettings = useCallback(async (): Promise<AdminSettings> => {
    if (isDevelopment) {
      return { interestRate: 0.15, entityId: activeEntity ?? "caseking", level1ApproverEmails: [], level2ApproverEmails: [], isGlobalAdmin: true, entities: ["caseking", "ocuk"], entityDomains: { "caseking.de": "caseking", "overclockers.co.uk": "ocuk" } };
    }
    const axiosInstance = await getAuthenticatedAxios();
    try {
      const response = await axiosInstance.get<{ success: boolean; data: AdminSettings }>(
        "/api/v1/admin/settings"
      );
      return response.data.data;
    } catch (error) {
      console.error("Error fetching admin settings:", error);
      throw error;
    }
  }, [getAuthenticatedAxios]);

  const updateAdminSettings = useCallback(
    async (settings: Partial<AdminSettings>): Promise<AdminSettings> => {
      if (isDevelopment) {
        return {
          interestRate: settings.interestRate ?? 0.15,
          entityId: activeEntity ?? "caseking",
          isGlobalAdmin: true,
          entities: ["caseking", "ocuk"],
          entityDomains: settings.entityDomains ?? { "caseking.de": "caseking", "overclockers.co.uk": "ocuk" },
          level1ApproverEmails: settings.level1ApproverEmails ?? [],
          level2ApproverEmails: settings.level2ApproverEmails ?? [],
        };
      }
      const axiosInstance = await getAuthenticatedAxios();
      try {
        const response = await axiosInstance.put<{ success: boolean; data: AdminSettings }>(
          "/api/v1/admin/settings",
          settings
        );
        return response.data.data;
      } catch (error) {
        console.error("Error updating admin settings:", error);
        const serverMessage = axios.isAxiosError(error) ? error.response?.data?.error : undefined;
        if (typeof serverMessage === "string" && serverMessage) {
          throw new ApiError(serverMessage);
        }
        throw error;
      }
    },
    [getAuthenticatedAxios]
  );

  // Stable identity: components list the client in effect dependencies, so a
  // new object per render would re-run those effects on every render.
  return useMemo(
    () => ({
      getMyRequests,
      getOrderData,
      getCFROIData,
      requestApproval,
      getApprovalDetails,
      getAdminApprovals,
      getAdminStatistics,
      getAdminSettings,
      updateAdminSettings,
      getCurrentUser,
      approveRequest,
      rejectRequest,
    }),
    [
      getMyRequests,
      getOrderData,
      getCFROIData,
      requestApproval,
      getApprovalDetails,
      getAdminApprovals,
      getAdminStatistics,
      getAdminSettings,
      updateAdminSettings,
      getCurrentUser,
      approveRequest,
      rejectRequest,
    ]
  );
};
